import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { personName } from "@/lib/championships";
import { Input } from "@/components/ui/Input";
import { SearchSelect } from "@/components/ui/SearchSelect";

export interface PlayerOption {
  id: number;
  nombre: string;
  apellidos: string | null;
  clubId: number;
  club: { nombre: string };
}

interface ClubOption {
  id: number;
  nombre: string;
}

export type PlayerRef = { id: number } | { clubId: number; nombre: string; apellidos: string | null } | null;

// Un jugador de una pareja: uno que ya existe (búsqueda en servidor, con tope) o uno nuevo que se
// da de alta en un club al guardar. `equipoId` deja libres los jugadores de la pareja que se está
// editando (el resto de inscritos no se ofrecen: un jugador solo puede estar en una pareja).
export function PlayerPicker({
  label,
  championshipId,
  equipoId,
  initial,
  onChange,
}: {
  label: string;
  championshipId: number;
  equipoId?: number;
  initial?: PlayerOption | null;
  onChange: (ref: PlayerRef) => void;
}) {
  const [mode, setMode] = useState<"existente" | "nuevo">("existente");
  const [player, setPlayer] = useState<PlayerOption | null>(initial ?? null);
  const [club, setClub] = useState<ClubOption | null>(null);
  const [nombre, setNombre] = useState("");
  const [apellidos, setApellidos] = useState("");

  useEffect(() => {
    if (mode === "existente") onChange(player ? { id: player.id } : null);
    else onChange(club && nombre.trim() ? { clubId: club.id, nombre: nombre.trim(), apellidos: apellidos.trim() || null } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, player, club, nombre, apellidos]);

  return (
    <div className="flex flex-col gap-3 rounded-xl bg-ink-50/60 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium text-ink-600">{label}</span>
        <div className="flex rounded-lg border border-ink-100 bg-white p-0.5 text-xs font-semibold">
          {(["existente", "nuevo"] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`rounded-md px-2.5 py-1 transition-colors ${mode === m ? "bg-brand-600 text-white" : "text-ink-500 hover:bg-ink-50"}`}
            >
              {m === "existente" ? "Existente" : "Nuevo jugador"}
            </button>
          ))}
        </div>
      </div>

      {mode === "existente" ? (
        <SearchSelect<PlayerOption>
          value={player}
          onChange={setPlayer}
          queryKey={["championship", championshipId, "players", equipoId ?? null]}
          fetchOptions={async (search) =>
            (
              await api.get<PlayerOption[]>(`/championships/${championshipId}/players`, {
                params: { search, ...(equipoId ? { equipoId } : {}) },
              })
            ).data
          }
          getKey={(p) => p.id}
          getLabel={(p) => personName(p)}
          getHint={(p) => p.club.nombre}
          placeholder="Busca un jugador por nombre o club…"
          searchPlaceholder="Nombre, apellidos o club…"
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <SearchSelect<ClubOption>
              label="Club del jugador"
              value={club}
              onChange={setClub}
              queryKey={["championship", championshipId, "clubs"]}
              fetchOptions={async (search) => (await api.get<ClubOption[]>(`/championships/${championshipId}/clubs`, { params: { search } })).data}
              getKey={(c) => c.id}
              getLabel={(c) => c.nombre}
              placeholder="Busca el club…"
              searchPlaceholder="Nombre del club…"
              resultCap={50}
            />
          </div>
          <Input placeholder="Nombre" aria-label="Nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} className="w-full" />
          <Input placeholder="Apellidos" aria-label="Apellidos" value={apellidos} onChange={(e) => setApellidos(e.target.value)} className="w-full" />
        </div>
      )}
    </div>
  );
}
